import Phaser from 'phaser';
import { accountClient, signedInRole } from '../../account/accountUi';
import { listCommandLog, markInboxSeen } from '../../account/adminInbox';
import { ROLE_NAME_COLORS, inboxRest, type CommandLogRow } from '../../config/devCommandLog';
import { drawStarfield } from '../starfield';
import { drawAvatarIcon } from '../avatarIcon';
import { installDebugReturn } from '../debugMode';
import { ScrollList } from '../../ui/scrollList';
import { chapterLevelLabel, defToProject, findBorder } from '../../config/creator';
import {
  adminLevelDef,
  listChapterRequests,
  listLevelRequests,
  reviewChapter,
  reviewLevel,
  type ChapterRequest,
  type LevelRequest,
} from '../../account/explore';
import { creatorButton, creatorFlash, levelPreview } from '../creator/creatorUi';
import { canvasTextInput } from '../creator/textInput';

const COMMANDS_SUBTITLE = 'Every dev command sent to another player or to everyone.';
const REQUESTS_SUBTITLE = 'Levels waiting for approval before anyone can see them in Explore.';
const LIST_X = 110;
const LIST_W = 1040;
const LIST_TOP = 150;
const LIST_H = 520;
const AVATAR = 36;
const ROW_GAP = 10;
/** A /suggest row's map (spec 2026-11-03 §1). */
const SUGGEST_PREVIEW_W = 400;
const SUGGEST_PREVIEW_H = (SUGGEST_PREVIEW_W * 720) / 1280;

/**
 * AdminInboxScene — the admin's INBOX (spec 2026-10-24): every dev command
 * aimed at another player or at everyone, newest first, as "<name> has done
 * the command /give user34 bags 2". The name is coloured by role (lower dev
 * green, higher dev blue, admin gold) with the sender's leaderboard icon to
 * its right. The MESSAGES toggle at the top shows only /message commands.
 * Opening it marks everything seen, which clears the menu's red "!".
 *
 * REQUESTS (spec 2026-11-02 §4.2) swaps the list for the Explore review
 * queue: "<user> is asking for approval of their level <name>", the map as
 * it plays, APPROVE and DISAPPROVE (which asks for a reason the creator sees).
 *
 * Admins only: the RPCs refuse anyone else, and so does this screen.
 */
export class AdminInboxScene extends Phaser.Scene {
  private list?: ScrollList;
  private messagesOnly = false;
  private requestsMode = false;
  private subtitle?: Phaser.GameObjects.Text;
  private requestsBg?: Phaser.GameObjects.Graphics;
  private requestsText?: Phaser.GameObjects.Text;
  private reasonPanel: Phaser.GameObjects.GameObject[] = [];
  private reviewing = false;
  private status?: Phaser.GameObjects.Text;
  private toggleBg?: Phaser.GameObjects.Graphics;
  private toggleText?: Phaser.GameObjects.Text;
  private loadSeq = 0;
  private gone = false;
  /** The maps of the levels in /suggest rows, by code. */
  private suggestDefs = new Map<string, { name: string; def: Record<string, unknown> }>();

  constructor() {
    super({ key: 'AdminInboxScene' });
  }

  create(): void {
    this.messagesOnly = false;
    this.requestsMode = false;
    this.reasonPanel = [];
    this.reviewing = false;
    this.gone = false;
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => (this.gone = true));
    drawStarfield(this, 1280, 720);
    this.add
      .text(640, 44, 'INBOX', { fontSize: '38px', color: '#f5c518', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setName('inbox-title');
    this.subtitle = this.add
      .text(640, 80, COMMANDS_SUBTITLE, {
        fontSize: '15px',
        color: '#c8d2d8',
      })
      .setOrigin(0.5);

    const back = this.add
      .text(80, 50, '← BACK', {
        fontSize: '22px',
        color: '#cfe0bd',
        backgroundColor: '#15131f',
        padding: { x: 16, y: 12 },
      })
      .setOrigin(0.5)
      .setName('inbox-back')
      .setInteractive({ useHandCursor: true });
    back.on('pointerover', () => back.setColor('#f5c518'));
    back.on('pointerout', () => back.setColor('#cfe0bd'));
    back.on('pointerup', () => this.scene.start('MenuScene'));

    this.buildToggle();

    this.list = new ScrollList(this, {
      x: LIST_X,
      y: LIST_TOP,
      width: LIST_W,
      height: LIST_H,
      barX: LIST_X + LIST_W + 18,
      barWidth: 12,
      cullText: true,
    });
    this.status = this.add
      .text(640, LIST_TOP + 60, '', { fontSize: '20px', color: '#9aa6ad', fontStyle: 'italic' })
      .setOrigin(0.5)
      .setName('inbox-status');

    const client = accountClient();
    if (!client || signedInRole() !== 'admin') {
      this.status.setText('The inbox is for admins only.');
    } else {
      // Opening the inbox is what clears the "!".
      void markInboxSeen(client);
      void this.fetchRows();
    }

    installDebugReturn(this);
  }

  /** The MESSAGES pill: off shows every command, on only /message. */
  private buildToggle(): void {
    this.buildRequestsToggle();
    const x = 540;
    const y = 116;
    const w = 190;
    const h = 36;
    this.toggleBg = this.add.graphics();
    this.toggleText = this.add
      .text(x, y, '', { fontSize: '17px', color: '#ffffff', fontStyle: 'bold' })
      .setOrigin(0.5);
    const hit = this.add
      .zone(x, y, w, h)
      .setName('inbox-messages-toggle')
      .setInteractive({ useHandCursor: true });
    const draw = () => {
      const g = this.toggleBg!;
      g.clear();
      const on = this.messagesOnly && !this.requestsMode;
      g.fillStyle(on ? 0x2e9e4f : 0x2a2f3a, 1);
      g.fillRoundedRect(x - w / 2, y - h / 2, w, h, h / 2);
      g.lineStyle(2, 0xffffff, on ? 0.9 : 0.4);
      g.strokeRoundedRect(x - w / 2, y - h / 2, w, h, h / 2);
      this.toggleText!.setText(on ? '✓ MESSAGES' : 'MESSAGES');
    };
    this.drawMessagesToggle = draw;
    draw();
    hit.on('pointerup', () => {
      if (this.requestsMode) {
        this.requestsMode = false;
        this.drawRequestsToggle();
      } else {
        this.messagesOnly = !this.messagesOnly;
      }
      draw();
      void this.fetchRows();
    });
  }

  private drawMessagesToggle: () => void = () => undefined;

  /** The REQUESTS pill: the Explore review queue instead of the commands. */
  private buildRequestsToggle(): void {
    const x = 760;
    const y = 116;
    const w = 190;
    const h = 36;
    this.requestsBg = this.add.graphics();
    this.requestsText = this.add
      .text(x, y, '', { fontSize: '17px', color: '#ffffff', fontStyle: 'bold' })
      .setOrigin(0.5);
    const hit = this.add
      .zone(x, y, w, h)
      .setName('inbox-requests-toggle')
      .setInteractive({ useHandCursor: true });
    this.drawRequestsToggle();
    hit.on('pointerup', () => {
      if (this.reasonPanel.length > 0) return;
      this.requestsMode = !this.requestsMode;
      this.drawRequestsToggle();
      this.drawMessagesToggle();
      void (this.requestsMode ? this.fetchRequests() : this.fetchRows());
    });
  }

  private drawRequestsToggle(): void {
    const x = 760;
    const y = 116;
    const w = 190;
    const h = 36;
    const g = this.requestsBg!;
    g.clear();
    g.fillStyle(this.requestsMode ? 0x7b6cff : 0x2a2f3a, 1);
    g.fillRoundedRect(x - w / 2, y - h / 2, w, h, h / 2);
    g.lineStyle(2, 0xffffff, this.requestsMode ? 0.9 : 0.4);
    g.strokeRoundedRect(x - w / 2, y - h / 2, w, h, h / 2);
    this.requestsText!.setText(this.requestsMode ? '✓ REQUESTS' : 'REQUESTS');
    this.subtitle?.setText(this.requestsMode ? REQUESTS_SUBTITLE : COMMANDS_SUBTITLE);
  }

  // ── The Explore review queue ───────────────────────────────────────────────

  private async fetchRequests(): Promise<void> {
    const client = accountClient();
    if (!client || !this.list) return;
    const seq = ++this.loadSeq;
    this.status?.setText('Loading…');
    this.list.content.removeAll(true);
    const [r, rc] = await Promise.all([listLevelRequests(client), listChapterRequests(client)]);
    if (seq !== this.loadSeq || this.gone) return;
    if (!r.ok || !rc.ok) {
      this.status?.setText('Could not load the requests — try again.');
      return;
    }
    this.status?.setText(
      r.value.length + rc.value.length === 0 ? 'No levels or chapters are waiting for review.' : '',
    );
    this.fillRequests(r.value, rc.value);
  }

  private fillRequests(rows: LevelRequest[], chapters: ChapterRequest[] = []): void {
    const list = this.list!;
    list.content.removeAll(true);
    const ROW = 340;
    rows.forEach((req, i) => {
      const y = i * (ROW + ROW_GAP);
      const plate = this.add
        .rectangle(LIST_W / 2, y + ROW / 2, LIST_W - 4, ROW, i % 2 ? 0x161a24 : 0x1c2130, 0.95)
        .setStrokeStyle(1, 0x7b6cff, 0.8);
      const asking =
        req.kind === 'unban'
          ? `${req.username} is asking for an unban of their level ${req.name}`
          : `${req.username} is asking for approval of their level ${req.name}`;
      const head = this.add
        .text(14, y + 12, asking, {
          fontSize: '19px',
          color: '#ffffff',
          fontStyle: 'bold',
          wordWrap: { width: LIST_W - 200, useAdvancedWrap: true },
        })
        .setName(`inbox-request-${i}-text`);
      const code = this.add
        .text(LIST_W - 12, y + 12, `#${req.code}  ${shortTime(req.requestedAt)}`, {
          fontSize: '13px',
          color: '#8a96a0',
        })
        .setOrigin(1, 0);
      const project = defToProject(req.name, req.def);
      // Every word the level will show, as plain text (the preview is small).
      const words = project.pieces
        .filter((pc) => pc.layer === 'text')
        .map((pc) => `"${pc.text ?? ''}"`);
      // Every word the level will show: its textboxes and its description.
      const descr = req.description ? `\nDescription: "${req.description}"` : '\nNo description';
      const wordsText = this.add
        .text(
          640,
          y + 250,
          (words.length ? `Textboxes: ${words.join(', ')}` : 'No textboxes') + descr,
          {
            fontSize: '14px',
            color: '#c8d2d8',
            wordWrap: { width: LIST_W - 660, useAdvancedWrap: true },
          },
        )
        .setOrigin(0, 0)
        .setName(`inbox-request-${i}-words`);
      list.content.add(wordsText);
      const preview = levelPreview(this, project, 480);
      preview.setPosition(14 + 240, y + 52 + 135).setName(`inbox-request-${i}-preview`);
      list.content.add([plate, head, code, preview]);
      const approve = creatorButton(
        this,
        760,
        y + 130,
        'APPROVE',
        { name: `inbox-approve-${i}`, width: 220, height: 56, fill: 0x2e9e4f, depth: 1 },
        () => undefined,
      );
      const reject = creatorButton(
        this,
        760,
        y + 210,
        'DISAPPROVE',
        { name: `inbox-disapprove-${i}`, width: 220, height: 56, fill: 0xb23a3a, depth: 1 },
        () => undefined,
      );
      for (const [b, approveIt] of [
        [approve, true],
        [reject, false],
      ] as const) {
        b.bg.off('pointerup');
        b.bg.on('pointerup', (ptr: Phaser.Input.Pointer) => {
          if (list.dragged || !list.inView(ptr) || this.reviewing || this.reasonPanel.length > 0)
            return;
          if (approveIt) void this.review(req, true, '');
          else this.askReason(req);
        });
        list.content.add([b.bg, b.text]);
      }
    });
    let y = rows.length * (ROW + ROW_GAP);
    chapters.forEach((req, i) => {
      y += this.chapterCard(req, i, y, list) + ROW_GAP;
    });
    list.scrollToTop();
    list.setContentHeight(y);
  }

  /** A chapter request: its name, its levels as they will be labelled, APPROVE / DISAPPROVE. */
  private chapterCard(req: ChapterRequest, i: number, y: number, list: ScrollList): number {
    const lines = req.levels.map(
      (l, n) =>
        `${chapterLevelLabel(req.def.format, req.def.order, n)} — ${l.name ?? '(not live)'} #${l.code}`,
    );
    const body = this.add
      .text(14, y + 50, lines.join('\n'), {
        fontSize: '15px',
        color: '#c8d2d8',
        lineSpacing: 4,
      })
      .setName(`inbox-chapter-${i}-levels`);
    const h = Math.max(240, body.height + 80);
    const border = findBorder(req.def.border);
    const plate = this.add
      .rectangle(LIST_W / 2, y + h / 2, LIST_W - 4, h, i % 2 ? 0x161a24 : 0x1c2130, 0.95)
      .setStrokeStyle(3, border.color, 1);
    const asking =
      req.kind === 'unban'
        ? `${req.username} is asking for an unban of their chapter ${req.name}`
        : `${req.username} is asking for approval of their chapter ${req.name}`;
    const head = this.add
      .text(14, y + 12, asking, {
        fontSize: '19px',
        color: '#ffffff',
        fontStyle: 'bold',
        wordWrap: { width: LIST_W - 200, useAdvancedWrap: true },
      })
      .setName(`inbox-chapter-${i}-text`);
    const code = this.add
      .text(LIST_W - 12, y + 12, `#${req.code}  ${shortTime(req.requestedAt)}`, {
        fontSize: '13px',
        color: '#8a96a0',
      })
      .setOrigin(1, 0);
    const meta = this.add
      .text(LIST_W - 12, y + 34, `${border.label} border`, { fontSize: '13px', color: '#8a96a0' })
      .setOrigin(1, 0);
    list.content.add([plate, head, code, meta, body]);
    const approve = creatorButton(
      this,
      760,
      y + 90,
      'APPROVE',
      { name: `inbox-chapter-approve-${i}`, width: 220, height: 56, fill: 0x2e9e4f, depth: 1 },
      () => undefined,
    );
    const reject = creatorButton(
      this,
      760,
      y + 170,
      'DISAPPROVE',
      { name: `inbox-chapter-disapprove-${i}`, width: 220, height: 56, fill: 0xb23a3a, depth: 1 },
      () => undefined,
    );
    for (const [b, approveIt] of [
      [approve, true],
      [reject, false],
    ] as const) {
      b.bg.off('pointerup');
      b.bg.on('pointerup', (ptr: Phaser.Input.Pointer) => {
        if (list.dragged || !list.inView(ptr) || this.reviewing || this.reasonPanel.length > 0)
          return;
        if (approveIt) void this.review(req, true, '', true);
        else this.askReason(req, true);
      });
      list.content.add([b.bg, b.text]);
    }
    return h;
  }

  /** DISAPPROVE: why (the creator sees it once), then CONFIRM. */
  private askReason(req: LevelRequest | ChapterRequest, chapter = false): void {
    const objs = this.reasonPanel;
    objs.push(
      this.add.rectangle(640, 360, 1280, 720, 0x000000, 0.65).setDepth(50).setInteractive(),
      this.add.rectangle(640, 360, 700, 300, 0x15131f, 1).setStrokeStyle(3, 0xb23a3a).setDepth(51),
      this.add
        .text(640, 250, `Why is "${req.name}" not allowed?`, {
          fontSize: '24px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(52),
      this.add
        .text(640, 375, 'The player sees this the next time they play.', {
          fontSize: '15px',
          color: '#9aa6ad',
        })
        .setOrigin(0.5)
        .setDepth(52),
    );
    let reason = '';
    const input = canvasTextInput(
      this,
      { x: 320, y: 300, w: 640, h: 50 },
      { maxLength: 200, placeholder: 'Reason', testId: 'inbox-reason-input' },
      (v) => (reason = v),
    );
    const close = () => {
      input.remove();
      for (const o of objs.splice(0)) o.destroy();
    };
    const confirm = creatorButton(
      this,
      540,
      440,
      'CONFIRM',
      { name: 'inbox-reason-confirm', width: 170, height: 50, depth: 52, fill: 0xb23a3a },
      () => {
        if (!reason.trim()) {
          creatorFlash(this, 'Write a reason first', 180);
          return;
        }
        close();
        void this.review(req, false, reason.trim(), chapter);
      },
    );
    const cancel = creatorButton(
      this,
      740,
      440,
      'CANCEL',
      { name: 'inbox-reason-cancel', width: 170, height: 50, depth: 52, fill: 0x5c6168 },
      close,
    );
    objs.push(confirm.bg, confirm.text, cancel.bg, cancel.text);
    input.focus();
  }

  private async review(
    req: LevelRequest | ChapterRequest,
    approve: boolean,
    reason: string,
    chapter = false,
  ): Promise<void> {
    const client = accountClient();
    if (!client || this.reviewing) return;
    this.reviewing = true;
    const r = chapter
      ? await reviewChapter(client, req, approve, reason)
      : await reviewLevel(client, req, approve, reason);
    this.reviewing = false;
    if (this.gone) return;
    creatorFlash(
      this,
      r.ok
        ? approve
          ? `"${req.name}" is live in Explore`
          : `"${req.name}" was not approved`
        : r.error,
      660,
    );
    void this.fetchRequests();
  }

  private async fetchRows(): Promise<void> {
    const client = accountClient();
    if (!client || !this.list) return;
    const seq = ++this.loadSeq;
    this.status?.setText('Loading…');
    const r = await listCommandLog(client, this.messagesOnly);
    // A toggle pressed mid-load wins; a scene that has gone takes nothing.
    if (seq !== this.loadSeq || this.gone) return;
    if (!r.ok) {
      this.status?.setText('Could not load the inbox — try again.');
      return;
    }
    // A suggestion shows the level's map (spec 2026-11-03 §1): one fetch per code.
    const codes = [...new Set(r.value.filter((x) => x.command === 'suggest').map((x) => x.target))];
    const defs = await Promise.all(codes.map((c) => adminLevelDef(client, c)));
    if (seq !== this.loadSeq || this.gone) return;
    this.suggestDefs = new Map();
    codes.forEach((c, i) => {
      const d = defs[i];
      if (d?.def) this.suggestDefs.set(c, { name: d.name, def: d.def });
    });
    this.status?.setText(
      r.value.length === 0 ? (this.messagesOnly ? 'No messages yet.' : 'No commands yet.') : '',
    );
    this.fill(r.value);
  }

  private fill(rows: CommandLogRow[]): void {
    const list = this.list!;
    list.content.removeAll(true);
    let y = 0;
    rows.forEach((row, i) => {
      const name = this.add
        .text(14, 0, row.senderUsername, {
          fontSize: '18px',
          color: ROLE_NAME_COLORS[row.senderRole],
          fontStyle: 'bold',
        })
        .setOrigin(0, 0)
        .setName(`inbox-row-${i}-name`);
      // The sender's leaderboard icon, to the right of their name.
      const iconX = name.x + name.width + 8 + AVATAR / 2;
      const restX = iconX + AVATAR / 2 + 8;
      const rest = this.add
        .text(restX, 0, inboxRest(row), {
          fontSize: '18px',
          color: '#e8ecef',
          // Clear of the timestamp in the row's top-right corner.
          wordWrap: { width: LIST_W - restX - 160, useAdvancedWrap: true },
        })
        .setOrigin(0, 0)
        .setName(`inbox-row-${i}-text`);
      const when = this.add
        .text(LIST_W - 12, 0, shortTime(row.createdAt), { fontSize: '13px', color: '#8a96a0' })
        .setOrigin(1, 0);
      const sug = row.command === 'suggest' ? this.suggestDefs.get(row.target) : undefined;
      const textH = Math.max(AVATAR, rest.height);
      const rowH = textH + 16 + (sug ? SUGGEST_PREVIEW_H + 12 : 0);
      const plate = this.add
        .rectangle(LIST_W / 2, y + rowH / 2, LIST_W - 4, rowH, i % 2 ? 0x161a24 : 0x1c2130, 0.92)
        .setStrokeStyle(1, 0x2f3d4c, 1);
      name.setY(y + 8 + (AVATAR - name.height) / 2);
      rest.setY(y + 8 + (rest.height > AVATAR ? 0 : (AVATAR - rest.height) / 2));
      when.setY(y + 8);
      list.content.add([plate, name, rest, when]);
      if (sug) {
        const preview = levelPreview(this, defToProject(sug.name, sug.def), SUGGEST_PREVIEW_W);
        preview
          .setPosition(restX + SUGGEST_PREVIEW_W / 2, y + 8 + textH + 6 + SUGGEST_PREVIEW_H / 2)
          .setName(`inbox-row-${i}-preview`);
        list.content.add(preview);
      }
      const icon = row.senderAvatar ? drawAvatarIcon(this, row.senderAvatar, AVATAR, AVATAR) : null;
      if (icon) {
        icon.setPosition(iconX, y + 8 + AVATAR / 2);
        list.content.add(icon);
      } else {
        // No icon chosen: the empty circle the leaderboard draws.
        list.content.add(
          this.add.circle(iconX, y + 8 + AVATAR / 2, AVATAR / 2 - 2).setStrokeStyle(2, 0x4a5563, 1),
        );
      }
      y += rowH + ROW_GAP;
    });
    list.scrollToTop();
    list.setContentHeight(y);
  }
}

/** "Oct 24, 21:05" in the admin's own time zone. */
function shortTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
